/**
 * TopBar — sticky header: title, search (Cmd/Ctrl+K, hint hidden), AS Assistant, language
 * dropdown (RU/EN/AZ/KA), theme toggle, notifications dropdown (live unread
 * count), profile menu.
 */
import { useEffect, useState } from 'react'
import { Platform } from 'react-native'
import { usePathname, useRouter, type Href } from 'expo-router'
import { ScrollView, Stack, Text, XStack, YStack } from 'tamagui'

import { useApp, useUiPrefs } from '../i18n'
import { useAuth } from '../auth/useAuth'
import {
  useUnreadCount,
  useNotifications,
  useReadAllNotifications,
  useReadNotification,
} from '../features/notifications/useNotifications'
import { notifHref } from '../features/notifications/notifHref'
import { useMe } from '../features/users/useUsers'
import { timeAgo } from '../lib/format'
import { Avatar, Backdrop, Icon, IconButton, LanguageMenu } from '../ui'
import { pressable } from '../ui/primitives'
import { ROUTE_TITLE } from './nav'
import { palette } from '../theme/palette'
import { CommandPalette } from './CommandPalette'
import { AssistantPanel } from './AssistantPanel'
import { useAssistantAccess } from '../features/assistant/useAssistant'

function PrimaryButton({ label, icon, onPress }: { label?: string; icon: string; onPress?: () => void }) {
  return (
    <XStack
      onPress={onPress}
      cursor="pointer"
      animation="quick"
      height={36}
      alignItems="center"
      gap={7}
      paddingHorizontal={label ? 14 : 10}
      borderRadius={7}
      backgroundColor="$primary"
      hoverStyle={{ backgroundColor: '$primaryActive' }}
      shadowColor={palette.shadow}
      shadowOpacity={0.35}
      shadowRadius={12}
      shadowOffset={{ width: 0, height: 8 }}
    >
      <Icon name={icon} size={15} color="$onPrimary" />
      {label ? (
        <Text color="$onPrimary" fontWeight="600" fontSize={13}>
          {label}
        </Text>
      ) : null}
    </XStack>
  )
}

/** Notification row (live `/notifications`). */
interface NotifVM {
  id: string
  icon: string
  title: string
  body: string
  when: string
  unread: boolean
  href: string | null
}

const NOTIF_ICON: Record<string, string> = {
  mention: 'at-sign',
  task: 'circle-check-big',
  request: 'inbox',
  edo: 'inbox',
  community: 'users',
  news: 'newspaper',
  'payments.task_assigned': 'wallet',
  system: 'bell',
}
const notifIcon = (type: string): string => NOTIF_ICON[type] ?? 'bell'

export function TopBar({ isPhone }: { isPhone: boolean }) {
  const { l } = useApp()
  const { theme, toggleTheme } = useUiPrefs()
  const { logout } = useAuth()
  const { data: me } = useMe()
  const meName = me?.fullName || '—'
  const meRole = me?.jobTitle || '—'
  const router = useRouter()
  const pathname = usePathname()
  const [notifOpen, setNotifOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  const [langOpen, setLangOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [aiOpen, setAiOpen] = useState(false)
  // «AS Ассистент» — только когда админ включил ассистента и он доступен этому сотруднику.
  const aiEnabled = !!useAssistantAccess().data?.enabled

  // Global search shortcut — Cmd+K (macOS) or Ctrl+K (Windows/Linux) opens the
  // command palette on every OS (web only). The in-field hint is intentionally
  // not rendered: it always read "⌘K", which is meaningless on Windows/Linux.
  useEffect(() => {
    if (Platform.OS !== 'web') return
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault()
        setSearchOpen(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const { data: unread } = useUnreadCount()
  const { data: liveNotifs } = useNotifications()
  const readOne = useReadNotification()
  const readAll = useReadAllNotifications()
  const hasLiveNotifs = Array.isArray(liveNotifs) && liveNotifs.length > 0

  // Live notifications only — no demo fallback (empty until the API returns data).
  const notifItems: NotifVM[] = hasLiveNotifs
    ? liveNotifs!.map((n) => ({
        id: n.id,
        icon: notifIcon(n.type),
        title: n.title,
        body: n.body ?? '',
        when: timeAgo(n.createdAt),
        unread: !n.isRead,
        href: notifHref(n.link),
      }))
    : []

  const unreadCount = unread?.count ?? 0

  // Dynamic routes (e.g. /post/:id) live under a parent section for the heading.
  const titleKey =
    ROUTE_TITLE[pathname] ??
    (pathname.startsWith('/post') ? 'nav.news' : pathname.startsWith('/processes/') ? 'nav.processes' : 'nav.home')
  const title = l(titleKey)

  // Клик по уведомлению: непрочитанное — пометить; со ссылкой — открыть (и закрыть дропдаун).
  const openNotif = (n: NotifVM) => {
    if (n.unread) readOne.mutate(n.id)
    if (n.href) {
      setNotifOpen(false)
      router.push(n.href as Href)
    }
  }

  return (
    <>
    <XStack
      height={60}
      flexShrink={0}
      alignItems="center"
      gap={10}
      paddingHorizontal={isPhone ? 14 : 24}
      borderBottomWidth={1}
      borderColor="$borderSecondary"
      backgroundColor="$background"
      zIndex={30}
    >
      {/* page title — desktop/tablet only; on phone the right-side controls eat
          the row and the title truncates to one letter, and BottomNav already
          shows the section anyway */}
      {!isPhone && (
        <Text fontSize={18} fontWeight="700" color="$color" numberOfLines={1} flexShrink={1}>
          {title}
        </Text>
      )}

      <Stack flex={1} />

      {!isPhone && (
        <XStack
          onPress={() => setSearchOpen(true)}
          cursor="pointer"
          width={240}
          height={36}
          alignItems="center"
          gap={8}
          paddingHorizontal={12}
          borderRadius={7}
          borderWidth={1}
          borderColor="$borderColor"
          hoverStyle={{ borderColor: '$primary' }}
        >
          <Icon name="search" size={15} color="$muted" />
          <Text flex={1} fontSize={13} color="$muted">
            {l('top.searchShort')}
          </Text>
        </XStack>
      )}

      {isPhone && (
        <IconButton onPress={() => setSearchOpen(true)}>
          <Icon name="search" size={20} color="$muted" />
        </IconButton>
      )}

      {aiEnabled ? <PrimaryButton label={isPhone ? undefined : l('top.ai')} icon="sparkles" onPress={() => setAiOpen(true)} /> : null}

      {/* language — shared <LanguageMenu/>; controlled so it stays mutually
          exclusive with the notifications + profile menus */}
      <LanguageMenu
        open={langOpen}
        onOpenChange={(o) => {
          setLangOpen(o)
          if (o) {
            setNotifOpen(false)
            setProfileOpen(false)
          }
        }}
      />

      {/* theme */}
      <IconButton onPress={toggleTheme} role="button" aria-label={l('set.theme')}>
        <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={18} color="$muted" />
      </IconButton>

      {/* notifications */}
      <Stack position="relative">
        <IconButton
          onPress={() => { setNotifOpen((v) => !v); setProfileOpen(false); setLangOpen(false) }}
          role="button"
          aria-label={l('top.notifications')}
          aria-expanded={notifOpen}
        >
          <Icon name="bell" size={18} color="$muted" />
        </IconButton>
        {unreadCount > 0 && (
          <Stack position="absolute" top={2} right={2} minWidth={16} height={16} borderRadius={999} backgroundColor="$tone_red" alignItems="center" justifyContent="center" paddingHorizontal={3} borderWidth={1.5} borderColor="$background" animation="bouncy" enterStyle={{ scale: 0.3, opacity: 0 }} scale={1} opacity={1}>
            <Text fontSize={9} fontWeight="700" color="#fff">
              {unreadCount}
            </Text>
          </Stack>
        )}
        {notifOpen && (
          <>
            <Backdrop onPress={() => setNotifOpen(false)} />
            <YStack position="absolute" top={48} right={0} width={340} zIndex={50} backgroundColor="$bgElevated" borderWidth={1} borderColor="$borderSecondary" borderRadius={7} overflow="hidden" shadowColor={palette.shadow} shadowOpacity={0.18} shadowRadius={20} shadowOffset={{ width: 0, height: 12 }} animation="quicker" enterStyle={{ opacity: 0, y: -8, scale: 0.97 }} opacity={1} y={0} scale={1}>
              <XStack alignItems="center" justifyContent="space-between" paddingHorizontal={16} paddingVertical={12} borderBottomWidth={1} borderColor="$borderSecondary">
                <Text fontSize={14} fontWeight="700" color="$color">
                  {l('top.notifications')}
                </Text>
                {notifItems.length > 0 && (
                  <Text
                    fontSize={12}
                    color="$primary"
                    fontWeight="600"
                    cursor="pointer"
                    onPress={() => { if (hasLiveNotifs) readAll.mutate() }}
                  >
                    {l('top.markRead')}
                  </Text>
                )}
              </XStack>
              <ScrollView style={{ maxHeight: 360 }}>
                {notifItems.length === 0 && (
                  <YStack paddingVertical={28} alignItems="center">
                    <Text fontSize={13} color="$muted">
                      {l('top.noNotifications')}
                    </Text>
                  </YStack>
                )}
                {/* защита от неограниченного списка без пагинации */}
                {notifItems.slice(0, 50).map((n) => {
                  // нажимается непрочитанное или со ссылкой; без onPress не выдаём ряд за button (PressCard-паттерн).
                  const clickable = hasLiveNotifs && (n.unread || !!n.href)
                  return (
                  <XStack
                    key={n.id}
                    {...pressable}
                    tag={clickable ? pressable.tag : undefined}
                    onPress={clickable ? () => openNotif(n) : undefined}
                    cursor={clickable ? 'pointer' : 'default'}
                    gap={11}
                    paddingHorizontal={16}
                    paddingVertical={11}
                    borderBottomWidth={1}
                    borderColor="$borderSecondary"
                    backgroundColor={n.unread ? '$primaryBg' : 'transparent'}
                    role={clickable ? 'button' : undefined}
                    aria-label={clickable ? n.title : undefined}
                  >
                    <Stack width={30} height={30} borderRadius={999} backgroundColor="$bgLayout" alignItems="center" justifyContent="center">
                      <Icon name={n.icon} size={15} color="$primary" />
                    </Stack>
                    <YStack flex={1}>
                      <Text fontSize={13} color="$muted" lineHeight={18}>
                        <Text color="$color" fontWeight="600">{n.title}</Text>{n.body ? ` ${n.body}` : ''}
                      </Text>
                      <Text fontSize={11} color="$muted" marginTop={2}>
                        {n.when}
                      </Text>
                    </YStack>
                    {n.unread && <Stack width={7} height={7} borderRadius={999} backgroundColor="$primary" marginTop={6} />}
                  </XStack>
                  )
                })}
              </ScrollView>
            </YStack>
          </>
        )}
      </Stack>

      {/* profile */}
      <Stack position="relative">
        <Stack
          {...pressable}
          onPress={() => { setProfileOpen((v) => !v); setNotifOpen(false); setLangOpen(false) }}
          cursor="pointer"
          aria-label={l('top.profile')}
          aria-expanded={profileOpen}
        >
          <Avatar name={meName} seed="me" size="md" uri={me?.avatarUrl} />
        </Stack>
        {profileOpen && (
          <>
            <Backdrop onPress={() => setProfileOpen(false)} />
            <YStack position="absolute" top={48} right={0} width={240} zIndex={50} backgroundColor="$bgElevated" borderWidth={1} borderColor="$borderSecondary" borderRadius={7} padding={6} shadowColor={palette.shadow} shadowOpacity={0.18} shadowRadius={20} shadowOffset={{ width: 0, height: 12 }} animation="quicker" enterStyle={{ opacity: 0, y: -8, scale: 0.97 }} opacity={1} y={0} scale={1}>
              <XStack gap={10} alignItems="center" padding={8}>
                <Avatar name={meName} seed="me" size="md" uri={me?.avatarUrl} />
                <YStack flex={1}>
                  <Text fontSize={13} fontWeight="600" color="$color" numberOfLines={1}>
                    {meName}
                  </Text>
                  <Text fontSize={11} color="$muted" numberOfLines={1}>
                    {meRole}
                  </Text>
                </YStack>
              </XStack>
              <Stack height={1} backgroundColor="$borderSecondary" marginVertical={6} />
              <MenuItem icon="user" label={l('top.profile')} onPress={() => { setProfileOpen(false); router.push('/profile') }} />
              <MenuItem icon="settings" label={l('nav.settings')} onPress={() => { setProfileOpen(false); router.push('/settings') }} />
              <Stack height={1} backgroundColor="$borderSecondary" marginVertical={6} />
              <MenuItem icon="log-out" label={l('top.logout')} danger onPress={() => { setProfileOpen(false); void logout() }} />
            </YStack>
          </>
        )}
      </Stack>
    </XStack>
    <CommandPalette open={searchOpen} onClose={() => setSearchOpen(false)} />
    {aiEnabled ? <AssistantPanel open={aiOpen} onClose={() => setAiOpen(false)} /> : null}
    </>
  )
}

function MenuItem({ icon, label, onPress, danger }: { icon: string; label: string; onPress: () => void; danger?: boolean }) {
  return (
    <XStack
      {...pressable}
      onPress={onPress}
      cursor="pointer"
      alignItems="center"
      gap={10}
      paddingHorizontal={12}
      paddingVertical={8}
      borderRadius={5}
      hoverStyle={{ backgroundColor: '$backgroundHover' }}
      role="menuitem"
    >
      <Icon name={icon} size={15} color={danger ? '$tone_red' : '$muted'} />
      <Text fontSize={13} color={danger ? '$tone_red' : '$color'}>
        {label}
      </Text>
    </XStack>
  )
}
